import { Component, Inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormControl, NgForm, Validators } from '@angular/forms';

import { MingaPermission } from 'libs/domain';
import { gateway, legacy_pb } from 'libs/generated-grpc-web';

import { PermissionsService } from '@app/src/app/permissions';

import {
  MODAL_OVERLAY_DATA,
  ModalOverlayRef,
  ModalOverlayServiceCloseEventType,
} from '@shared/components/modal-overlay';
import { SystemAlertSnackBarService } from '@shared/components/system-alert-snackbar';

export interface IBroadcastNotificationDialogResponse {
  canceled?: boolean;
  successOverlay?: TemplateRef<BroadcastNotificationDialog>;
}

@Component({
  selector: 'mg-broadcast-notification-dialog',
  templateUrl: './BroadcastNotification.component.html',
  styleUrls: ['./BroadcastNotification.component.scss'],
})
export class BroadcastNotificationDialog implements OnInit {
  public pushNoteTitle: string;
  public pushNoteBody: string;
  public translateParams: any;
  public loading: boolean;
  public isIOS: boolean = window.MINGA_DEVICE_IOS;
  public enableGlobalBroadcast: boolean;
  public broadcastGlobally: boolean;
  public currentMingaName: string;
  public confirmString = 'ALL';
  public confirmStringInput: string;
  public shouldSendAsEmail: boolean;

  public titleControl: UntypedFormControl;
  public bodyControl: UntypedFormControl;

  @ViewChild('successOverlayTemplate', { static: true })
  successOverlayTemplate: TemplateRef<BroadcastNotificationDialog>;

  @ViewChild('form', { read: NgForm, static: true })
  form: NgForm;

  constructor(
    @Inject(MODAL_OVERLAY_DATA) public data: any,
    private _modalRef: ModalOverlayRef<BroadcastNotificationDialog>,
    private _systemAlertSnackBar: SystemAlertSnackBarService,
    private _permissions: PermissionsService,
    private _notificationManager: gateway.notification_ng_grpc_pb.NotificationManager,
  ) {
    if (data.mingaName) {
      this.currentMingaName = data.mingaName;
      this.translateParams = { value: data.mingaName };
    } else {
      this.currentMingaName = 'this Minga';
      this.translateParams = { value: 'this Minga' };
    }
  }

  ngOnInit() {
    this.initForm();
  }

  get titleControlErrorKey() {
    return this.titleControl.errors ? Object.keys(this.titleControl.errors)[0] : '';
  }

  get bodyControlErrorKey() {
    return this.bodyControl.errors ? Object.keys(this.bodyControl.errors)[0] : '';
  }

  onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.submit();
    }
  }

  close() {
    this._modalRef.close(ModalOverlayServiceCloseEventType.CLOSE);
  }

  initForm() {
    const commonValidators = [
      Validators.minLength(1),
      // MgValidators.NoSymbols,
    ];

    this.titleControl = new UntypedFormControl(
      this.pushNoteTitle,
      commonValidators.concat([Validators.maxLength(120)]),
    );
    this.bodyControl = new UntypedFormControl(this.pushNoteBody, commonValidators.concat([Validators.maxLength(1024)]));

    this.form.form.addControl('pushNoteTitle', this.titleControl);
    this.form.form.addControl('pushNoteBody', this.bodyControl);
    this.form.form.markAsPristine();
  }

  async initPermissions() {
    this.enableGlobalBroadcast = this._permissions.hasPermission(MingaPermission.SUPERADMIN);
  }

  isSubmitDisabled() {
    return (
      (this.form && (this.form.pristine || !this.form.valid)) ||
      (!this.pushNoteTitle && !this.pushNoteBody) ||
      (this.broadcastGlobally && this.confirmStringInput !== this.confirmString) ||
      this.loading
    );
  }

  async submit() {
    if (this.isSubmitDisabled()) {
      return false;
    }
    this.loading = true;

    const request = new gateway.notification_pb.BroadcastNotificationRequest();
    request.setNotificationTitle(this.pushNoteTitle);
    request.setNotificationBody(this.pushNoteBody);
    // request.setAllMingasBroadcast(this.broadcastGlobally);
    request.setSendEmail(this.shouldSendAsEmail);

    const response = await this._notificationManager.sendBroadcast(request);
    const status = response.getStatus();

    if (status === legacy_pb.StatusCode.OK) {
      this._modalRef.close(ModalOverlayServiceCloseEventType.SUBMIT);
      this._systemAlertSnackBar.success('Alert Sent!');
    } else if (status === legacy_pb.StatusCode.INSUFFICIENT_PERMISSIONS) {
      this._systemAlertSnackBar.error(`You have insufficient permissions for this content`);
    }
  }

  onBroadcastToggleChange() {
    if (this.broadcastGlobally) {
      this.translateParams = { value: 'ALL Mingas' };
    } else {
      this.translateParams = { value: this.currentMingaName };
    }
  }
}
